import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import Pagination from "@/components/base/Pagination";
import { filterMovies } from "@/utils/content";
import { genreOptions, regionOptions, yearOptions } from "@/mocks/home";

const typeMeta: Record<string, { title: string; intro: string; filterType: "movie" | "tv" | "all"; sort: "latest" | "rating" | "hot" }> = {
  movie: {
    title: "电影",
    intro: "院线新片、口碑佳作与经典电影，按类型、地区、年份快速筛选，发现你想看的那一部。",
    filterType: "movie",
    sort: "latest",
  },
  tv: {
    title: "电视剧",
    intro: "热播剧集、年度剧王与高分好剧，追踪剧情走向，找到适合一口气追完的作品。",
    filterType: "tv",
    sort: "latest",
  },
  recommend: {
    title: "编辑精选推荐",
    intro: "由编辑团队精心挑选的必看片单，兼顾口碑与人气，帮你告别选择困难。",
    filterType: "all",
    sort: "rating",
  },
  hot: {
    title: "热播榜单",
    intro: "当下人气飙升的影视作品排行，实时感受大家都在追的热门内容。",
    filterType: "all",
    sort: "hot",
  },
};

const PAGE_SIZE = 8;

export default function Category() {
  const { type = "movie" } = useParams();
  const meta = typeMeta[type] || typeMeta.movie;

  const [genre, setGenre] = useState("全部");
  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [sort, setSort] = useState<"latest" | "rating" | "hot">(meta.sort);
  const [page, setPage] = useState(1);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort(meta.sort);
    setPage(1);
    setVisible(PAGE_SIZE);
  }, [type, meta.sort]);

  const results = useMemo(
    () => filterMovies({ type: meta.filterType, genre, region, year, sort }),
    [meta.filterType, genre, region, year, sort]
  );

  const totalPages = Math.ceil(results.length / PAGE_SIZE);
  const pageItems = results.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const overPageItems = results.slice(0, visible);

  const resetFilters = () => {
    setGenre("全部");
    setRegion("全部");
    setYear("全部");
    setSort(meta.sort);
    setPage(1);
  };

  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPage(1);
    setVisible(PAGE_SIZE);
  };

  const selectClass =
    "px-3 py-2 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-800 focus:outline-none focus:ring-2 focus:ring-accent-400/60 cursor-pointer";

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: meta.title }]} />

      <div className="mt-4 rounded-lg bg-primary-950 px-6 py-8 md:py-10 relative overflow-hidden">
        <div className="relative z-10">
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
            {meta.title}
          </h1>
          <p className="mt-2 text-sm md:text-base text-background-200 max-w-2xl">{meta.intro}</p>
        </div>
        <i className="ri-film-line absolute -right-4 -bottom-6 text-[120px] text-primary-900"></i>
      </div>

      {/* 筛选栏 */}
      <div className="mt-6 rounded-lg bg-background-50 border border-background-200">
        <button
          type="button"
          onClick={() => setFilterOpen((v) => !v)}
          className="md:hidden w-full flex items-center justify-between px-4 py-3 cursor-pointer"
        >
          <span className="flex items-center gap-2 text-sm font-label text-foreground-800">
            <i className="ri-filter-3-line"></i>
            筛选条件
          </span>
          <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
        </button>
        <div
          className={`${filterOpen ? "flex" : "hidden"} md:flex flex-col md:flex-row md:items-center gap-3 p-4`}
        >
          <div className="flex items-center gap-2 flex-1 flex-wrap">
            <select
              value={genre}
              onChange={(e) => onFilterChange(setGenre)(e.target.value)}
              className={selectClass}
            >
              {genreOptions.map((g) => (
                <option key={g} value={g}>
                  类型：{g}
                </option>
              ))}
            </select>
            <select
              value={region}
              onChange={(e) => onFilterChange(setRegion)(e.target.value)}
              className={selectClass}
            >
              {regionOptions.map((r) => (
                <option key={r} value={r}>
                  地区：{r}
                </option>
              ))}
            </select>
            <select
              value={year}
              onChange={(e) => onFilterChange(setYear)(e.target.value)}
              className={selectClass}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  年份：{y}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as "latest" | "rating" | "hot")}
              className={selectClass}
            >
              <option value="latest">排序：最新</option>
              <option value="rating">排序：评分</option>
              <option value="hot">排序：热度</option>
            </select>
            <button
              type="button"
              onClick={resetFilters}
              className="px-3 py-2 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-accent-400 hover:text-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              重置
            </button>
          </div>
        </div>
      </div>

      <p className="mt-4 text-xs md:text-sm text-foreground-500 font-label">
        共找到 <span className="text-accent-600 font-semibold">{results.length}</span> 部作品
      </p>

      {results.length === 0 ? (
        <div className="mt-8 rounded-lg bg-background-50 border border-background-200 py-16 flex flex-col items-center">
          <i className="ri-inbox-line text-5xl text-background-400"></i>
          <p className="mt-4 text-sm text-foreground-600">暂无符合条件内容，换个筛选试试？</p>
          <button
            type="button"
            onClick={resetFilters}
            className="mt-5 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-label font-semibold hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            重置筛选条件
          </button>
        </div>
      ) : (
        <>
          <div className="mt-4 hidden md:grid grid-cols-4 gap-4">
            {pageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>

          <div className="mt-4 md:hidden grid grid-cols-2 gap-3">
            {overPageItems.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>

          <div className="hidden md:block mt-8">
            <Pagination
              current={page}
              total={totalPages}
              onChange={(p) => {
                setPage(p);
                setVisible(PAGE_SIZE);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </div>

          <div className="md:hidden mt-6">
            {visible < results.length ? (
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="w-full py-3 rounded-md border border-background-300 text-sm text-foreground-700 hover:border-accent-400 hover:text-accent-600 transition-colors cursor-pointer"
              >
                加载更多
              </button>
            ) : (
              <p className="text-center text-xs text-foreground-500">已经到底啦</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}